import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { useToaster } from '../../shared/composables/use-toaster';
import { getOwnerFullName } from '../../site-owner/utils/owner.util';
import { CurrentSiteStore } from '../current-site.store';
import { DeviceId } from '../models/Device';
import { SiteId } from '../models/Site';
import { SiteDevicesStore } from '../site-devices.store';

/** The messages about the commands sent to the devices. A label is a translation key. */
export function useDeviceCommandToasts() {
    const currentSite = inject(CurrentSiteStore);
    const siteDevices = inject(SiteDevicesStore);
    const router = inject(Router);
    const translate = inject(TranslateService);
    const toaster = useToaster();

    // No client name once the installation is no longer the one on screen.
    const refused = (siteId: SiteId, deviceName: string, subtitle: string) => {
        const site = currentSite.current(siteId);
        toaster.error({ title: deviceName, subtitle, message: site && getOwnerFullName(site.owners[0]) });
    };

    /** A real-time outcome is only shown for a device of the installation on screen, with a link to it. */
    const onScreen = (siteId: SiteId, deviceId: DeviceId) => {
        const site = currentSite.current(siteId);
        const device = siteDevices.devices().find((d) => d.id === deviceId);
        if (!site || !device) return null;

        return {
            title: device.name,
            message: getOwnerFullName(site.owners[0]),
            onClick: () => void router.navigate([`/gateway/${site.gatewayId}`], { queryParams: { deviceId } }),
        };
    };

    const failed = (siteId: SiteId, deviceId: DeviceId, subtitle: string, stateLabel: string, duration: number) => {
        const device = onScreen(siteId, deviceId);
        if (!device) return;

        toaster.error({
            ...device,
            subtitle: translate.instant(subtitle, { stateLabel: translate.instant(stateLabel) }),
            duration,
        });
    };

    return {
        settingRefused(siteId: SiteId, deviceName: string, stateLabel: string): void {
            const stateLabelText = translate.instant(stateLabel);
            refused(
                siteId,
                deviceName,
                translate.instant('TOASTER_DEVICE_UPSATE_STATE_INVALID', { stateLabel: stateLabelText }),
            );
        },

        settingFailed(siteId: SiteId, deviceId: DeviceId, stateLabel: string, queued: boolean): void {
            const subtitle = queued
                ? 'TOASTER_DEVICE_UPSATE_STATE_VALID_QUEUED'
                : 'TOASTER_DEVICE_UPSATE_STATE_INVALID';
            failed(siteId, deviceId, subtitle, stateLabel, 45 * 1000);
        },

        settingSaved(siteId: SiteId, deviceId: DeviceId): void {
            const device = onScreen(siteId, deviceId);
            if (!device) return;

            toaster.success({
                title: translate.instant('TOASTER_DEVICE_SETTING_UPDATE_SUCCESS_TITLE'),
                onClick: device.onClick,
            });
        },

        refreshRefused(siteId: SiteId, deviceName: string, sectionTitle: string): void {
            const sectionLabel = translate.instant(sectionTitle);
            refused(siteId, deviceName, translate.instant('EQUIPMENT_REFRESH_TOAST_ERROR', { sectionLabel }));
        },

        refreshFailed(siteId: SiteId, deviceId: DeviceId, stateLabel: string, queued: boolean): void {
            const subtitle = queued
                ? 'TOASTER_DEVICE_REFRESH_STATE_VALID_QUEUED'
                : 'TOASTER_DEVICE_REFRESH_STATE_INVALID';
            failed(siteId, deviceId, subtitle, stateLabel, 15 * 1000);
        },

        refreshCompleted(): void {
            toaster.success({
                title: translate.instant('EQUIPMENT_DETAIL_STATE_SECTION_UPDATE_SUCCESS_TITLE'),
                message: translate.instant('EQUIPMENT_DETAIL_STATE_SECTION_UPDATE_SUCCESS_MESSAGE'),
            });
        },
    };
}
